import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Form, Button, Row, Col, Spinner, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { QuestionCircle } from 'react-bootstrap-icons';

// Dev-only "Tune Tree Detection" tool. Lets the people building the
// inference service probe the tree-detection API with different params,
// see the exact request + raw response, and overwrite the lead's trees.
// Gated behind REACT_APP_ENABLE_DEBUG_TOOLS (see the trigger button in
// Trees.js).

const DEFAULTS = {
  device: 'gpu',
  postprocess: true,
};

const FIELD_INFO = {
  device: 'cpu or gpu (GPU).',
  postprocess: 'Run post-processing (uses the DSM to compute per-tree height).',
};

const InfoTip = ({ id, text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${id}`}>{text}</Tooltip>}>
    <QuestionCircle className="ml-1 text-muted" style={{ cursor: 'help' }} />
  </OverlayTrigger>
);

InfoTip.propTypes = {
  id: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function TreeTuner({
  show, onClose, runTreeTune, reloadCanvasObstacles,
}) {
  const [form, setForm] = useState(DEFAULTS);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null); // { ok, statusCode, count, elapsedMs, finishedAt, rawResponse }
  const [error, setError] = useState(null);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Build the request body that will actually be sent. All fields are set in
  // this form (pre-filled with defaults), so we send all of them.
  const requestBody = useMemo(() => ({
    device: form.device,
    postprocess: form.postprocess,
  }), [form]);

  const formatFinishedAt = (finishedAt) => {
    if (!finishedAt) return '';
    const d = new Date(finishedAt);
    if (Number.isNaN(d.getTime())) return String(finishedAt);
    return d.toLocaleTimeString();
  };

  const handleRun = () => {
    setIsRunning(true);
    setError(null);
    runTreeTune(requestBody)
      .then((data) => {
        const raw = data?.raw_response ?? {};
        const count = data?.count ?? raw?.count ?? (Array.isArray(raw?.trees) ? raw.trees.length : 0);
        setResult({
          ok: true,
          statusCode: data?.status_code,
          count,
          elapsedMs: data?.elapsed_ms,
          finishedAt: data?.finished_at,
          rawResponse: raw,
        });
        // Endpoint already persisted the trees server-side; reload the
        // canvas so they re-render (reuses the design tool's canvas reload).
        if (typeof reloadCanvasObstacles === 'function') {
          return reloadCanvasObstacles();
        }
        return null;
      })
      .catch((err) => {
        const data = err?.response?.data;
        setError(data?.error || err?.message || 'Tree detection failed.');
        if (data) {
          setResult({
            ok: false,
            statusCode: data.status_code ?? err?.response?.status,
            elapsedMs: data.elapsed_ms,
            rawResponse: data.raw_response ?? data,
          });
        }
      })
      .finally(() => setIsRunning(false));
  };

  const renderSummary = () => {
    if (!result) return null;
    const mark = result.ok ? '✓' : '✗';
    const seconds = Number.isFinite(result.elapsedMs) ? `${(result.elapsedMs / 1000).toFixed(1)}s` : '';
    const parts = [
      `${mark} ${result.statusCode ?? ''}`.trim(),
      result.ok ? `${result.count ?? 0} trees` : null,
      seconds || null,
      formatFinishedAt(result.finishedAt) || null,
    ].filter(Boolean);
    return (
      <div className={result.ok ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>
        {parts.join(' · ')}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Tune Tree Detection</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>
                  device
                  <InfoTip id="device" text={FIELD_INFO.device} />
                </Form.Label>
                <Form.Control
                  as="select"
                  value={form.device}
                  onChange={(e) => setField('device', e.target.value)}
                >
                  <option value="cpu">cpu</option>
                  <option value="gpu">gpu</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="postprocess"
                  label={(
                    <>
                      postprocess
                      <InfoTip id="postprocess" text={FIELD_INFO.postprocess} />
                    </>
                  )}
                  checked={form.postprocess}
                  onChange={(e) => setField('postprocess', e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>

        <div className="mt-2">
          <Form.Label className="font-weight-bold mb-1">Request</Form.Label>
          <pre
            className="border rounded bg-light p-2 mb-0"
            style={{ maxHeight: '160px', overflow: 'auto', fontSize: '0.8rem' }}
          >
            {JSON.stringify(requestBody, null, 2)}
          </pre>
        </div>

        {error && !result?.rawResponse && (
          <div className="text-danger mt-3">{error}</div>
        )}

        {result && (
          <div className="mt-3">
            <Form.Label className="font-weight-bold mb-1">Results</Form.Label>
            {renderSummary()}
            <pre
              className="border rounded bg-light p-2 mt-1 mb-0"
              style={{ maxHeight: '240px', overflow: 'auto', fontSize: '0.8rem' }}
            >
              {JSON.stringify(result.rawResponse, null, 2)}
            </pre>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isRunning}>
          Close
        </Button>
        <Button variant="primary" onClick={handleRun} disabled={isRunning}>
          {isRunning ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Running...
            </>
          ) : (
            'Run detection'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

TreeTuner.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  runTreeTune: PropTypes.func.isRequired,
  reloadCanvasObstacles: PropTypes.func,
};

export default TreeTuner;
